import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import LogoIcon from "./assets/images/logo.js";
import ATRReportStyles from "./styles/ATRReportStyles.js";
import RiskAdverse from "../../../assets/images/ATR Profiler/Risk Averse.jpg";
import Conservative from "../../../assets/images/ATR Profiler/Conservative.jpg";
import Balanced from "../../../assets/images/ATR Profiler/Balanced.jpg";
import Moderate from "../../../assets/images/ATR Profiler/Moderate.jpg";
import Dynamic from "../../../assets/images/ATR Profiler/Dynamic.jpg";
import Adventurous from "../../../assets/images/ATR Profiler/Adventurous.png";

/**
 * Generates a PDF report for the Attitude to Risk (ATR) questionnaire using react-pdf.
 *
 * The report includes:
 * - Customer and adviser details
 * - Questionnaire responses
 * - Calculated and chosen risk categories
 * - Detailed risk profile descriptions
 * - Potential investment returns and growth scenarios
 * - A summary table of all risk profiles
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array<Object>} props.responses - Array of response objects for each question, each containing a `label`.
 * @param {number} props.totalScore - The total score calculated from the questionnaire responses.
 * @param {string} props.riskCategory - The calculated risk category based on questionnaire responses.
 * @param {string} [props.customCategory] - The risk category chosen by the customer (optional).
 * @param {string} props.customerName - The name of the customer.
 * @param {string} props.adviserName - The name of the adviser preparing the report.
 * @param {Array<Object>} props.questions - Array of question objects, each containing a `text` property.
 * @param {Function} props.formatDate - Function to format the current date for display in the report.
 * @param {string} props.tradingName - The trading name to display in the report header.
 * @param {string} [props.logo] - Optional logo image URL to display in the report header.
 * @param {string} props.primaryColor - The primary color used for styling the report.
 *
 * @returns {JSX.Element} The rendered PDF document as a React component.
 */
const ReportPDF = ({
  responses,
  totalScore,
  riskCategory,
  customCategory,
  customerName,
  adviserName,
  questions,
  formatDate,
  tradingName,
  logo,
  primaryColor,
}) => {
  const riskRatings = [
    {
      riskRating: "Risk Averse",
      description: [
        "Risk Averse investors typically have very limited knowledge of financial matters. They are unlikely to have experience of investment.",
        "Risk Averse investors prefer knowing that their capital is safe rather than seeking high returns. They are not comfortable with the thought of investing in the stockmarket and want to keep their money in the bank.",
        "Risk Averse investors can take a long time to make up their mind on financial matters and will usually suffer from severe regret if their decisions turn out badly. Risk Averse investors typically hold all of their money in cash deposits. Risk averse investors need to be aware that their unwillingness to take any risk with their money may mean that the value of their savings does not keep pace with rises in the cost of living (inflation).",
      ],
      potentialReturns: [
        "There are no guarantees with this investment strategy and actual results achieved may be higher or lower than those shown. However, these probable returns are a sensible way of understanding the potential downsides as well as the upsides when saving or investing this way.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing. The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, nor does it take into consideration the impact of inflation but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £127,544 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £108,006 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £93,907. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Conservative",
      description: [
        "Conservative investors typically have fairly limited knowledge of financial matters. They are unlikely to have much experience of investment.",
        "In general, Conservative investors prefer knowing that their capital is safe rather than seeking high returns. They are not particularly comfortable with the thought of investing in the stockmarket and would rather keep most of their money in lower risk assets.",
        "Conservative investors can take a relatively long time to make up their mind on financial matters and will usually suffer from regret if their decisions turn out badly.",
      ],
      potentialReturns: [
        "When investing, you must accept there will be times when the value of your portfolio will fall. There will be good years and bad years. The important thing is to understand the potential volatility of this investment strategy, what a bad year might look like and be able to cope with it.",
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 7% or worse of your investment before charges (equivalent to a £7,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
        "But remember, there are no guarantees. Also, it is important to be aware that your actual investments will be different - Fund Managers will not hold the same investments as the illustrative asset allocation, and so will give rise to different outcomes for potential loss/volatility and potential returns.",
        "There are no guarantees with this investment strategy and actual results achieved may be higher or lower than those shown. As the investment strategies increase in risk and potential reward, so does the amount you may lose in a bad year. Conversely the investment gain in a good year also increases. However, we believe these probable returns are a sensible way of understanding the potential downsides as well as the upsides when saving or investing this way.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing. The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £156,617 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £126,681 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £94,675. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Balanced",
      description: [
        "Balanced investors typically have modest levels of knowledge about financial matters. They may have some experience of investment in riskier assets.",
        "In general, Balanced investors prefer not to take much risk with their investments, but will do so to an extent. They prefer lower risk assets, but realise riskier investments are likely to give better longer term returns.",
        "Balanced investors can take some time to make up their mind on financial matters and can often suffer from regret when decisions turn out badly.",
      ],
      potentialReturns: [
        "When investing, you must accept there will be times when the value of your portfolio will fall. There will be good years and bad years. The important thing is to understand the potential volatility of this investment strategy, what a bad year might look like and be able to cope with it.",
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 10% or worse of your investment before charges (equivalent to a £10,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
        "But remember, there are no guarantees. Also, it is important to be aware that your actual investments will be different - Fund Managers will not hold the same investments as the illustrative asset allocation, and so will give rise to different outcomes for potential loss/volatilityand potential returns.",
        "There are no guarantees with this investment strategy and actual results achieved may be higher or lower than those shown. As the investment strategies increase in risk and potential reward, so does the amount you may lose in a bad year. Conversely the investment gain in a good year also increases. However, we believe these probable returns are a sensible way of understanding the potential downsides as well as the upsides when saving or investing this way.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing. The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £187,605 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £136,651 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £87,488. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Moderate",
      description: [
        "Moderate investors typically have a degree of knowledge about financial matters. They usually have some experience of investment, including investing in products containing higher risk assets such as equities.",
        "In general, Moderate investors understand that they have to take investment risk in order to be able to meet their long-term goals. They are likely to be willing to take risk with a high proportion of their available assets.",
        "Moderate investors will usually be able to make up their minds on financial matters relatively quickly, but still suffer from some feelings of regret when their decisions turn out badly.",
      ],
      potentialReturns: [
        "When investing, you must accept there will be times when the value of your portfolio will fall. There will be good years and bad years. The important thing is to understand the potential volatility of this investment strategy, what a bad year might look like and be able to cope with it.",
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 15% or worse of your investment before charges (equivalent to a £15,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
        "But remember, there are no guarantees. Also, it is important to be aware that your actual investments will be different - Fund Managers will not hold the same investments as the illustrative asset allocation, and so will give rise to different outcomes for potential loss/volatility and potential returns.",
        "There are no guarantees with this investment strategy and actual results achieved may be higher or lower than those shown. As the investment strategies increase in risk and potential reward, so does the amount you may lose in a bad year. Conversely the investment gain in a good year also increases. However, we believe these probable returns are a sensible way of understanding the potential downsides as well as the upsides when saving or investing this way.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing.",
        "The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £223,258 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £145,842 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £78,198. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Dynamic",
      description: [
        "Dynamic investors typically have quite high levels of financial knowledge. They will usually be experienced investors, who have used a range of investment products in the past.",
        "In general, Dynamic investors are happy to take investment risk and understand this is crucial in terms of generating long-term return. They are willing to take risk with most of their available assets.",
        "Dynamic investors will usually be able to make up their minds on financial matters quite quickly. While they can suffer from regret when their decisions turn out badly, they are able to accept that occasional poor returns are a necessary part of longterm investment.",
      ],
      potentialReturns: [
        "When investing, you must accept there will be times when the value of your portfolio will fall. There will be good years and bad years. The important thing is to understand the potential volatility of this investment strategy, what a bad year might look like and be able to cope with it.",
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 20% or worse of your investment before charges (equivalent to a £20,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
        "But remember, there are no guarantees. Also, it is important to be aware that your actual investments will be different - Fund Managers will not hold the same investments as the illustrative asset allocation, and so will give rise to different outcomes for potential loss/volatility and potential returns.",
        // "There are no guarantees with this investment strategy and actual results achieved may be higher or lower than those shown. As the investment strategies increase in risk and potential reward, so does the amount you may lose in a bad  year. Conversely the investment gain in a good year also increases. However, we believe these probable returns are a sensible way of understanding the potential downsides as well as the upsides when saving or investing this way.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing.",
        "The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £261,233 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £153,700 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £69,429. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Adventurous",
      description: [
        "Adventurous investors typically have high levels of financial knowledge. They often have substantial amounts of investment experience and may have been active in managing their investment arrangements.",
        "In general, Adventurous investors are looking for a high return on their capital and are willing to take considerable amounts of risk to achieve this. They are usually willing to take risk with all of their available assets.",
        "Adventurous investors typically will make up their minds on financial matters quickly. They do not suffer from regret to any great extent and can accept occasional poor returns without much difficulty.",
      ],
      potentialReturns: [
        "When investing, you must accept there will be times when the value of your portfolio will fall. There will be good years and bad years. The important thing is to understand the potential volatility of this investment strategy, what a bad year might look like and be able to cope with it.",
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 25% or worse of your investment before charges (equivalent to a £25,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
        "But remember, there are no guarantees. Also, it is important to be aware that your actual investments will be different - Fund Managers will not hold the same investments as the illustrative asset allocation, and so will give rise to different outcomes for potential loss/volatility and potential returns.",
        "There are no guarantees with this investment strategy and actual results achieved may be higher or lower than those shown. As the investment strategies increase in risk and potential reward, so does the amount you may lose in a bad year. Conversely the investment gain in a good year also increases. However, we believe these probable returns are a sensible way of understanding the potential downsides as well as the upsides when saving or investing this way.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing.",
        "The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £316,919 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £163,454 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £60,865. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
  ];

  const selectedCategory = customCategory ? customCategory : riskCategory;
  const selectedRiskProfile = riskRatings.find(
    (rating) => rating.riskRating === selectedCategory
  );

  const resultedCategory = riskRatings.find(
    (rating) => rating.riskRating === riskCategory
  );

  const categoryImages = {
    "Risk Averse": RiskAdverse,
    Conservative: Conservative,
    Balanced: Balanced,
    Moderate: Moderate,
    Dynamic: Dynamic,
    Adventurous: Adventurous,
  };

  const imageSrc = categoryImages[customCategory];
  const styles = ATRReportStyles(primaryColor);

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.graphicWrapper}>
          <View style={styles.graphicBox} />
          <View style={styles.graphicBox1} />
          <View style={styles.graphicBox2} />
          <View style={styles.graphicBox3} />
        </View>
        <View>
          {/* Date at the top */}

          {/* Centered content */}
          <View style={styles.centeredContent}>
            {logo ? <Image style={styles.image} src={logo} /> : <LogoIcon />}
            {/* <LogoIcon /> */}
            {/* <Image style={styles.image1} src={LogoIcon} /> */}
            <Text style={styles.header}>{tradingName}</Text>
            <Text style={styles.subheading}>
              Attitude to Risk Report for: {customerName || "Customer Name"}
            </Text>
            <Text style={styles.subheading}>
              Prepared By: {adviserName || "Adviser Name"}
            </Text>
            <Text style={styles.subheading}>{formatDate()}</Text>
          </View>
        </View>

        <Text break style={styles.content}>
          It is important to understand the level of risk you are prepared to
          take with your investments. When discussing your attitude to risk you
          answered a series of statements using a risk questionnaire.
        </Text>
        <Text style={styles.content}>
          The risk questionnaire has been developed to help understand your risk
          profile and assist me in the design and recommendation of your
          investment portfolio. The results in this report will be used
          alongside other information about you in order to generate your
          personal investment recommendations.
        </Text>
        <Text style={styles.subheading}>What is Risk?</Text>
        <Text style={styles.content}>
          Risk means that there could be a number of different possible outcomes
          associated with a particular action or activity and we do not know
          beforehand which one will occur, such as making a financial loss or
          the chances of not meeting your goals and objectives. The concern here
          is with financial risk i.e. the volatility associated with prices of
          and returns from investments.
        </Text>
        <Text style={styles.subheading}>
          How has the risk questionnaire been designed?
        </Text>
        <Text style={styles.content}>
          Through the application of psychometric tests, the science of
          measuring individuals' attitudes, a series of statements designed to
          capture several aspects of risk attitude:
        </Text>
        <Text style={styles.content}>
          1. Financial Knowledge - generally, individuals who are more
          knowledgeable about finance and investment are more willing to accept
          investment risk. Knowledgeable investors are likely to understand that
          some element of risk is required to generate higher returns and that
          short-term fluctuations need not matter to meet longer-term goals.
        </Text>
        <Text style={styles.content}>
          2. Comfort with Risk - some individuals have psychological traits that
          allow them to take risk. These individuals typically see risk as
          involving a 'thrill' or 'opportunity' rather than as 'danger' or a
          'loss'.
        </Text>
        <Text style={styles.content}>
          3. Investment Choice - risk attitude can also be gauged in preferences
          for different types of investments e.g. the relative safety of a bank
          account versus the potential risk of stocks & shares.
        </Text>
        <Text style={styles.content}>
          4. Regret - is the negative emotion that arises from making a decision
          that is, after the fact, wrong.
        </Text>
        <Text break style={styles.subheading}>
          What does the risk questionnaire tell you?
        </Text>
        <Text style={styles.content}>
          It is designed to give you a representative view of an investor who
          has answered the statements as you have by equally weighting the
          answers to the various statements and providing an attitude to risk
          profile.
        </Text>
        <Text style={styles.content}>
          However, you should not rely solely on the initial results of
          answering the statements and the subsequent risk profile provided.
          Other factors such as your investment objectives and your risk
          capacity i.e. your ability financially to 'shoulder the risk' of
          potential loss and cope with it must also be taken into account.
        </Text>
        <Text style={styles.content}>
          This report confirms your risk profile as a result of you answering
          the statements and our subsequent discussions to fully determine
          whether the risk profile is suitable for you, having discussed your
          personal circumstances, attitudes and financial objectives.
        </Text>
        <Text style={styles.subheading}>Investment returns and volatility</Text>
        <Text style={styles.content}>
          It is important to understand that investment returns cannot usually
          be guaranteed and that any investment strategy brings with it the
          potential for loss especially in the short term. The higher the risk
          profile, the greater potential returns but also the potential for
          higher losses - particularly in any one year. Each risk profile has a
          typical range of returns and you should use this to determine your own
          attitude to potential loss and, therefore, which risk profile best
          describes your risk attitude.
        </Text>
        <Text style={styles.content}>
          At the back of this report you will find all the risk profile
          descriptions. If, having reviewed these again, you change your mind
          about your agreed risk profile please let me know straight away so
          that I can take any appropriate action.
        </Text>
        <Text break style={styles.subheading}>
          Your Questionnaire Responses:
        </Text>
        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeader, styles.questionCell]}>
              Question
            </Text>
            <Text style={[styles.tableHeader, styles.responseCell]}>
              Response
            </Text>
          </View>

          {/* Table Rows */}
          {questions.map((q, index) => (
            <View
              key={index}
              style={[
                styles.tableRow,
                index % 2 !== 0 && styles.alternateRow, // Apply grey background for alternate rows
              ]}
            >
              <Text style={styles.questionCell}>{q.text}</Text>
              <Text style={styles.responseCell}>
                {responses[index]?.label || "Not Answered"}
              </Text>
            </View>
          ))}
        </View>
        {/* <Text style={styles.result}>Total Score: {totalScore}</Text> */}
        <Text style={styles.content}>
          Having reviewed the answers to the risk profile statements, the risk
          category has been calculated and the results suggest you are an
          <Text style={styles.subcontent}> {riskCategory} </Text>
          investor.
        </Text>
        {resultedCategory?.description.map((point, index) => (
          <Text key={index} style={styles.content}>
            {point}
          </Text>
        ))}
        <Text break style={styles.subheading}>
          Your Risk Profile
        </Text>
        <Text style={styles.content}>
          Having discussed your personal circumstances, attitudes and financial
          objectives, your <Text style={styles.subcontent}>CHOSEN</Text> risk
          rating is as follows:
        </Text>
        <Text style={styles.subheading}>
          Your chosen Attitude to Risk -{" "}
          {customCategory ? customCategory : riskCategory} Investor
        </Text>
        {selectedRiskProfile?.description.map((point, index) => (
          <Text key={index} style={styles.content}>
            {point}
          </Text>
        ))}
        <Text style={styles.content}>
          Risk attitude is only one factor in determining a suitable investment
          strategy. You must also consider your ability to withstand short term
          losses, and your need to take risk to achieve your financial goals.
        </Text>
        <Text style={styles.content}>
          You should always discuss appropriate investments with your financial
          adviser. Your decision to make any investments should have regard to
          the way your money is to be managed, the impact of inflation and the
          length of time you wish to invest.
        </Text>
        <Text style={styles.subheading}>
          Potential Returns - {customCategory ? customCategory : riskCategory}{" "}
          Investor
        </Text>
        {selectedRiskProfile?.potentialReturns.map((point, index) => (
          <Text key={index} style={styles.content}>
            {point}
          </Text>
        ))}
        <Text style={styles.subheading}>Potential Gain</Text>
        {selectedRiskProfile?.potentialGain.map((point, index) => (
          <Text key={index} style={styles.content}>
            {point}
          </Text>
        ))}
        <Image src={imageSrc} />
        <View>
          <Text style={[styles.subcontent, { color: "#9eb25d" }]}>
            Potential Growth
          </Text>
        </View>
        {selectedRiskProfile?.potentialGrowth.map((point, index) => (
          <Text key={index} style={styles.content}>
            {point}
          </Text>
        ))}
        <View>
          <Text style={[styles.subcontent, { color: "#f1db4b" }]}>
            Average Growth
          </Text>
        </View>
        {selectedRiskProfile?.averageGrowth.map((point, index) => (
          <Text key={index} style={styles.content}>
            {point}
          </Text>
        ))}
        <View>
          <Text style={[styles.subcontent, { color: "#cc0202" }]}>
            Lower Growth
          </Text>
        </View>
        {selectedRiskProfile?.lowerGrowth.map((point, index) => (
          <Text key={index} style={styles.content}>
            {point}
          </Text>
        ))}
        <Text style={styles.content}>
          There are no guarantees with this investment strategy and actual
          results achieved may be higher or lower than those shown. As the
          investment strategies increase in risk and potential reward, so does
          the amount you may lose in a bad year. Conversely the investment gain
          in a good year also increases. However, we believe these probable
          returns are a sensible way of understanding the potential downsides as
          well as the upsides when saving or investing this way.
        </Text>
        <Text break style={styles.subheading}>
          All Risk Profiles
        </Text>
        <Text style={styles.content}>
          All the risk profile descriptions associated with the risk
          questionnaire are described in the table below. You should read these
          descriptions as you might find another description better describes
          your attitude to investment risk, in which case you should let me know
          straight away.
        </Text>
        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeader, { flex: 1 }]}>Risk Rating</Text>
            <Text style={[styles.tableHeader, { flex: 3 }]}>Description</Text>
          </View>

          {/* Table Rows */}
          {riskRatings.map((row, index) => (
            <View
              key={index}
              style={[
                styles.tableRow,
                index % 2 !== 0 && styles.alternateRow, // Apply grey background for alternate rows
                { wrap: false },
              ]}
            >
              {/* Risk Rating Cell */}
              <Text style={styles.riskCell}>{row.riskRating}</Text>

              {/* Description Cell */}
              <View style={styles.descriptionCell}>
                {row.description.map((paragraph, pIndex) => (
                  <Text key={pIndex} style={styles.content}>
                    {paragraph}
                  </Text>
                ))}
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};
export default ReportPDF;
